/**
 * Custom metrics + logs push, with a small batched sender (interval + on page hide).
 * Public API: aris.metric(name, value, opts) and aris.log(level, message, tags).
 */
import { maskText } from '@aris/request-watcher-shared';
import type { ResolvedConfig } from './config.js';
import { uuid } from './util.js';

const SDK = { name: 'sdk-react', version: '0.1.0' };

function endpointFor(collectorUrl: string, suffix: string): string {
  if (/\/api\/events\/ingest\/?$/.test(collectorUrl)) {
    return collectorUrl.replace(/\/api\/events\/ingest\/?$/, suffix);
  }
  return `${collectorUrl.replace(/\/$/, '')}${suffix}`;
}

class SignalSender {
  private buf: Record<string, unknown>[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private url: string,
    private apiKey: string,
    private flushMs = 5000,
    private batch = 20,
  ) {}

  start(): void {
    this.timer = setInterval(() => void this.flush(), this.flushMs);
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flushBeacon());
      document?.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flushBeacon();
      });
    }
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    void this.flush();
  }

  enqueue(item: Record<string, unknown>): void {
    this.buf.push(item);
    if (this.buf.length >= this.batch) void this.flush();
  }

  async flush(): Promise<void> {
    if (this.buf.length === 0) return;
    const events = this.buf.splice(0, 100);
    try {
      const res = await fetch(this.url, {
        method: 'POST', keepalive: events.length <= 30,
        headers: { 'content-type': 'application/json', 'x-aris-api-key': this.apiKey },
        body: JSON.stringify({ sdk: SDK, events }),
      });
      if (!res.ok) throw new Error(String(res.status));
    } catch {
      this.buf.unshift(...events); // retry next tick
      if (this.buf.length > 1000) this.buf.splice(0, this.buf.length - 1000);
    }
  }

  private flushBeacon(): void {
    if (this.buf.length === 0) return;
    const events = this.buf.splice(0);
    try {
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(`${this.url}?api_key=${encodeURIComponent(this.apiKey)}`,
          new Blob([JSON.stringify({ sdk: SDK, events })], { type: 'application/json' }));
      }
    } catch { /* ignore */ }
  }
}

export type MetricType = 'counter' | 'gauge' | 'distribution';

export interface Signals {
  metric: (name: string, value: number, opts?: { type?: MetricType; tags?: Record<string, unknown> }) => void;
  log: (level: 'debug' | 'info' | 'warn' | 'error', message: string, tags?: Record<string, unknown>) => void;
  stop: () => void;
}

export function installSignals(cfg: ResolvedConfig): Signals {
  const metrics = new SignalSender(endpointFor(cfg.collectorUrl, '/api/metrics/ingest'), cfg.apiKey);
  const logs = new SignalSender(endpointFor(cfg.collectorUrl, '/api/logs/ingest'), cfg.apiKey);
  metrics.start();
  logs.start();

  return {
    metric: (name, value, opts) => {
      metrics.enqueue({
        id: uuid(), project_id: cfg.projectId, environment: cfg.environment,
        name, type: opts?.type ?? 'gauge', value, tags: opts?.tags ?? null,
        timestamp: new Date().toISOString(),
      });
    },
    log: (level, message, tags) => {
      logs.enqueue({
        id: uuid(), project_id: cfg.projectId, environment: cfg.environment,
        level, message: maskText(message, { maskPii: cfg.maskPii }) ?? message,
        logger: null, tags: tags ?? null, timestamp: new Date().toISOString(),
      });
    },
    stop: () => { metrics.stop(); logs.stop(); },
  };
}
